/**
 * Pure geographic helpers. No network, no I/O, no framework — just coordinates.
 */

export type LatLng = { lat: number; lng: number };

/** GeoJSON position: `[longitude, latitude]`. */
export type Position = [number, number];

export type LineString = { type: "LineString"; coordinates: Position[] };
export type MultiLineString = { type: "MultiLineString"; coordinates: Position[][] };
export type LineGeometry = LineString | MultiLineString;

const EARTH_RADIUS_KM = 6371;
const FEET_PER_METRE = 3.28084;
const KM_PER_MILE = 1.609344;

/** Unit conversions live here so a route's mileage reads the same everywhere it is printed. */
export const metresToFeet = (metres: number): number => metres * FEET_PER_METRE;
export const feetToMetres = (feet: number): number => feet / FEET_PER_METRE;
export const kmToMiles = (km: number): number => km / KM_PER_MILE;
export const milesToKm = (miles: number): number => miles * KM_PER_MILE;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Great-circle distance in kilometres. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** Narrow unknown GeoJSON to a line geometry we can walk. */
export function asLineGeometry(value: unknown): LineGeometry | null {
  if (!value || typeof value !== "object") return null;
  const geometry = value as { type?: unknown; coordinates?: unknown };
  if (!Array.isArray(geometry.coordinates)) return null;
  if (geometry.type === "LineString" || geometry.type === "MultiLineString") {
    return geometry as LineGeometry;
  }
  return null;
}

/** Normalize either line geometry into an array of segments. */
export function toSegments(geometry: unknown): Position[][] {
  const line = asLineGeometry(geometry);
  if (!line) return [];
  return line.type === "LineString" ? [line.coordinates] : line.coordinates;
}

/** Flatten a line geometry into its vertices, in order. */
export function toVertices(geometry: unknown): LatLng[] {
  const vertices: LatLng[] = [];
  for (const segment of toSegments(geometry)) {
    for (const position of segment) {
      if (Array.isArray(position) && position.length >= 2) {
        const [lng, lat] = position;
        if (typeof lng === "number" && typeof lat === "number") vertices.push({ lat, lng });
      }
    }
  }
  return vertices;
}

export type RouteSample = {
  /** Points spaced along the route; their circles together cover the corridor. */
  points: LatLng[];
  radiusKm: number;
  /** True when the sample came from real geometry rather than the trailhead fallback. */
  fromGeometry: boolean;
};

/**
 * Approximate a buffer around a trail line as a handful of sample points with a
 * shared radius. Unioning small circle queries at these points covers the whole
 * route, which is how we ask a point-radius API about a linear feature.
 *
 * Falls back to the trailhead and a wide radius when no geometry is available.
 */export function sampleRoutePoints(
  geometry: unknown,
  trailhead: LatLng,
  maxPoints = 6,
): RouteSample {
  const fallback: RouteSample = { points: [trailhead], radiusKm: 5, fromGeometry: false };

  const vertices = toVertices(geometry);
  if (vertices.length < 2) return fallback;

  const cumulative = [0];
  for (let i = 1; i < vertices.length; i++) {
    cumulative.push(cumulative[i - 1]! + haversineKm(vertices[i - 1]!, vertices[i]!));
  }
  const total = cumulative[cumulative.length - 1]!;
  if (total < 0.6) return { points: [vertices[0]!], radiusKm: 1.5, fromGeometry: true };

  const count = Math.min(maxPoints, Math.max(2, Math.ceil(total / 2) + 1));
  const points: LatLng[] = [];
  for (let i = 0; i < count; i++) {
    const target = (total * i) / (count - 1);
    let index = 0;
    while (index < cumulative.length - 1 && cumulative[index]! < target) index++;
    points.push(vertices[index]!);
  }

  // Overlap the circles slightly so gaps between samples stay covered.
  const spacing = total / (count - 1);
  const radiusKm = Math.min(2.5, Math.max(1.0, spacing / 2 + 0.4));
  return { points, radiusKm, fromGeometry: true };
}

/** A point on an ordered path, with how far along the path it sits. */
export type PathPoint = { point: LatLng; distanceKm: number };

const endpointsOf = (segment: LatLng[]): [LatLng, LatLng] =>
  [segment[0]!, segment[segment.length - 1]!];

/**
 * Stitch unordered line segments into a single ordered path, starting nearest
 * `start`.
 *
 * Overpass returns every way whose name matched, in arbitrary order and
 * direction, so the raw geometry is a bag of segments rather than a route.
 * Walking it in order is a prerequisite for anything distance-based, such as an
 * elevation profile.
 *
 * A matched trail name usually covers a small network rather than one line:
 * alongside the main trail come junction stubs, connectors, viewpoint spurs and
 * alternates. Three rules keep the walk on the through-route:
 *
 * - segments shorter than `minSegmentKm` are dropped, removing junction stubs;
 * - at each junction the candidate reaching *furthest from the start* wins, so
 *   the walk keeps heading out rather than doubling back up a viewpoint spur;
 * - length breaks ties, favouring the main trail over a short alternate.
 *
 * The result is a plausible walking line, not a guaranteed reconstruction.
 */
/**
 * Below this, two ways are treated as joined rather than merely near.
 *
 * OpenStreetMap shares nodes between connecting ways, so a real junction is
 * zero; this only absorbs floating-point noise.
 */
const JOIN_KM = 0.01;

/** Length of an ordered list of points, in kilometres. */
export function pathLengthKm(path: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += haversineKm(path[i - 1]!, path[i]!);
  return total;
}

/** Cumulative distance from the start of a path, in kilometres. */
export function cumulativeDistancesKm(path: LatLng[]): number[] {
  const distances = [0];
  for (let i = 1; i < path.length; i++) {
    distances.push(distances[i - 1]! + haversineKm(path[i - 1]!, path[i]!));
  }
  return distances;
}

/**
 * Resample a path to at most `maxPoints` evenly spaced by distance.
 *
 * A named OSM trail can run to thousands of vertices, far more than an
 * elevation service will accept in one request and more than a chart can show.
 */
export function resamplePath(path: LatLng[], maxPoints: number): PathPoint[] {
  if (path.length === 0 || maxPoints < 2) return [];
  if (path.length <= maxPoints) {
    const distances = cumulativeDistancesKm(path);
    return path.map((point, i) => ({ point, distanceKm: distances[i]! }));
  }

  const distances = cumulativeDistancesKm(path);
  const total = distances[distances.length - 1]!;
  if (total <= 0) return [{ point: path[0]!, distanceKm: 0 }];

  const sampled: PathPoint[] = [];
  let cursor = 0;
  for (let i = 0; i < maxPoints; i++) {
    const target = (total * i) / (maxPoints - 1);
    while (cursor < distances.length - 1 && distances[cursor]! < target) cursor++;
    sampled.push({ point: path[cursor]!, distanceKm: distances[cursor]! });
  }
  return sampled;
}

export type ElevationStats = {
  gainM: number;
  lossM: number;
  minM: number;
  maxM: number;
};

/**
 * Smooth a series with a centred moving average.
 *
 * Terrain models are sampled on a coarse grid (~90 m for global DEMs), so a
 * profile taken along a switchbacking trail picks up oscillation that is an
 * artefact of the grid rather than real undulation. Averaging suppresses that
 * without flattening a sustained climb.
 */
export function smoothSeries(values: number[], window = 3): number[] {
  if (window <= 1 || values.length <= window) return [...values];
  const half = Math.floor(window / 2);

  return values.map((_, i) => {
    const from = Math.max(0, i - half);
    const to = Math.min(values.length - 1, i + half);
    let sum = 0;
    for (let j = from; j <= to; j++) sum += values[j]!;
    return sum / (to - from + 1);
  });
}

/**
 * Total ascent and descent along a series of elevations.
 *
 * Terrain models carry metre-scale noise, and summing every rise would inflate
 * the gain badly over thousands of samples. Only moves exceeding `thresholdM`
 * from the last confirmed level are counted.
 */
export function elevationStats(elevations: number[], thresholdM = 10): ElevationStats {
  const known = elevations.filter((value) => Number.isFinite(value));
  if (known.length === 0) return { gainM: 0, lossM: 0, minM: 0, maxM: 0 };

  let gainM = 0;
  let lossM = 0;
  let reference = known[0]!;
  // Tracked in the same pass rather than by spreading `known` into Math.min:
  // a full-resolution recording runs to six figures of samples, well past the
  // argument limit that makes a spread throw.
  let minM = known[0]!;
  let maxM = known[0]!;

  for (const elevation of known) {
    if (elevation < minM) minM = elevation;
    if (elevation > maxM) maxM = elevation;
    const delta = elevation - reference;
    if (delta >= thresholdM) {
      gainM += delta;
      reference = elevation;
    } else if (delta <= -thresholdM) {
      lossM -= delta;
      reference = elevation;
    }
  }

  return {
    gainM: Math.round(gainM),
    lossM: Math.round(lossM),
    minM: Math.round(minM),
    maxM: Math.round(maxM),
  };
}

/** How a walked route returns to its start. */
export type RouteShape = "loop" | "out-and-back";

/** A line that never returns to its start is one-way; a walk of it is not. */
export type WalkShape = RouteShape | "one-way";

/** Ends closer than this are the same place. */
export const CLOSED_ROUTE_KM = 0.2;

/**
 * A closed line retraces itself when the way back lies on the way out. Mean
 * separation below this is GPS scatter on one trail; above it, two trails.
 */
const RETRACE_KM = 0.075;

const COMPARISON_POINTS = 32;

/**
 * The point a given distance along the path. Interpolated rather than snapped
 * to a vertex: segments can run long, and snapping would make two probes of the
 * same ground land a whole segment apart.
 */
function pointAt(points: LatLng[], distances: number[], targetKm: number): LatLng {
  let index = 0;
  while (index < distances.length - 2 && distances[index + 1]! < targetKm) index++;
  const from = points[index]!;
  const to = points[index + 1] ?? from;
  const fromKm = distances[index]!;
  const toKm = distances[index + 1] ?? fromKm;
  const t = toKm > fromKm ? Math.min(1, Math.max(0, (targetKm - fromKm) / (toKm - fromKm))) : 0;
  return { lat: from.lat + (to.lat - from.lat) * t, lng: from.lng + (to.lng - from.lng) * t };
}

/**
 * How a line that returns to its start got there: round a loop, or back the way
 * it came. Endpoints cannot tell these apart, but the shapes chart differently —
 * an out-and-back has a turnaround, and its high point sits midway.
 *
 * Compared by walking the first half forwards and the second half backwards: on
 * an out-and-back the two runs cover the same ground.
 */
export function detectWalkShape(
  points: LatLng[],
  distances: number[],
  toleranceKm = CLOSED_ROUTE_KM,
): WalkShape {
  const totalKm = distances[distances.length - 1]!;
  if (haversineKm(points[0]!, points[points.length - 1]!) > toleranceKm) return "one-way";

  let separationKm = 0;
  for (let i = 0; i <= COMPARISON_POINTS; i++) {
    const alongKm = (totalKm / 2) * (i / COMPARISON_POINTS);
    separationKm += haversineKm(
      pointAt(points, distances, alongKm),
      pointAt(points, distances, totalKm - alongKm),
    );
  }

  return separationKm / (COMPARISON_POINTS + 1) <= RETRACE_KM ? "out-and-back" : "loop";
}

/**
 * Close a walked route so it starts and ends in the same place.
 *
 * A hike returns to the car: either the line already comes back on itself (a
 * loop) or it is walked in both directions (an out-and-back). A one-way line
 * describes half the day, so anything measured from it — distance, ascent, an
 * elevation profile — needs the return leg added first.
 *
 * When the far end is already within `toleranceKm` of the start the route is
 * treated as a loop and returned unchanged; otherwise the reverse leg is
 * appended, without repeating the turnaround point.
 */
export function closeRoute(
  path: LatLng[],
  toleranceKm = CLOSED_ROUTE_KM,
): { path: LatLng[]; shape: RouteShape } {
  const start = path[0];
  const end = path[path.length - 1];
  if (!start || !end || path.length < 2) return { path, shape: "out-and-back" };

  if (haversineKm(start, end) <= toleranceKm) return { path, shape: "loop" };

  const back = [...path].reverse().slice(1);
  return { path: [...path, ...back], shape: "out-and-back" };
}

/**
 * A stretch of a line that covers ground the line already covered, heading the
 * same way. `from` and `to` are indices into the points, inclusive.
 */
export type RepeatedStretch = { from: number; to: number; km: number };

/** Closer than this, two passes are on the same trail. Snapped lines repeat exactly; GPS wanders a little. */
const REPEAT_TOLERANCE_KM = 0.02;

/**
 * Below this angle between them, two passes head the same way: cos 45°.
 * Wide enough for a recording's wobble, far from the 180° of a walk back.
 */
const SAME_WAY_COS = Math.SQRT1_2;

/** Shorter repeats are a pause, a detour round a log, or switchbacks brushing past each other. */
const MIN_REPEAT_KM = 0.15;

/** Repeats split by less than this are one stretch with a stray segment in it. */
const REPEAT_BRIDGE_KM = 0.05;

/**
 * Where a line walks ground it has already walked, in the same direction.
 *
 * Covering trail twice is normal: an out-and-back walks all of it twice and a
 * lollipop walks its stem twice, but always once each way. Covering it twice
 * the same way is not — it means the line went back down and then up again,
 * as a mis-tapped waypoint does — and on a map it is invisible, because the
 * second pass sits exactly on top of the first. Comparing direction rather
 * than mere overlap is what keeps every honest out-and-back out of this.
 *
 * Each segment's midpoint is tested against the segments walked before it,
 * through a grid so a long recording is not compared pairwise.
 */
export function repeatedStretches(points: LatLng[]): RepeatedStretch[] {
  if (points.length < 3) return [];

  // Flat projection in kilometres: fine over the few kilometres of a hike.
  const kx = (Math.PI / 180) * EARTH_RADIUS_KM * Math.cos(toRadians(points[0]!.lat));
  const ky = (Math.PI / 180) * EARTH_RADIUS_KM;
  const xs = points.map((p) => (p.lng - points[0]!.lng) * kx);
  const ys = points.map((p) => (p.lat - points[0]!.lat) * ky);
  const along = cumulativeDistancesKm(points);

  const cell = REPEAT_TOLERANCE_KM * 2;
  const key = (cx: number, cy: number) => `${cx},${cy}`;
  const grid = new Map<string, number[]>();
  const segments = points.length - 1;
  for (let j = 0; j < segments; j++) {
    const left = Math.floor((Math.min(xs[j]!, xs[j + 1]!) - REPEAT_TOLERANCE_KM) / cell);
    const right = Math.floor((Math.max(xs[j]!, xs[j + 1]!) + REPEAT_TOLERANCE_KM) / cell);
    const bottom = Math.floor((Math.min(ys[j]!, ys[j + 1]!) - REPEAT_TOLERANCE_KM) / cell);
    const top = Math.floor((Math.max(ys[j]!, ys[j + 1]!) + REPEAT_TOLERANCE_KM) / cell);
    for (let cx = left; cx <= right; cx++) {
      for (let cy = bottom; cy <= top; cy++) {
        const bucket = grid.get(key(cx, cy));
        if (bucket) bucket.push(j);
        else grid.set(key(cx, cy), [j]);
      }
    }
  }

  const repeated: boolean[] = [];
  for (let i = 0; i < segments; i++) {
    const dx = xs[i + 1]! - xs[i]!;
    const dy = ys[i + 1]! - ys[i]!;
    const length = Math.hypot(dx, dy);
    repeated.push(false);
    if (length <= 0) continue;
    const mx = xs[i]! + dx / 2;
    const my = ys[i]! + dy / 2;

    for (const j of grid.get(key(Math.floor(mx / cell), Math.floor(my / cell))) ?? []) {
      // Only ground walked well before: a segment's neighbours always lie close.
      if (along[j + 1]! > along[i]! - 2 * REPEAT_TOLERANCE_KM) continue;
      const ex = xs[j + 1]! - xs[j]!;
      const ey = ys[j + 1]! - ys[j]!;
      const other = Math.hypot(ex, ey);
      if (other <= 0) continue;
      if ((dx * ex + dy * ey) / (length * other) < SAME_WAY_COS) continue;
      const t = Math.min(1, Math.max(0, ((mx - xs[j]!) * ex + (my - ys[j]!) * ey) / other ** 2));
      if (Math.hypot(mx - xs[j]! - t * ex, my - ys[j]! - t * ey) <= REPEAT_TOLERANCE_KM) {
        repeated[i] = true;
        break;
      }
    }
  }

  // Runs of repeated segments, bridged over stray gaps, kept when long enough to matter.
  const stretches: RepeatedStretch[] = [];
  let i = 0;
  while (i < segments) {
    if (!repeated[i]) {
      i++;
      continue;
    }
    const from = i;
    let to = i;
    let j = i + 1;
    while (j < segments) {
      if (repeated[j]) {
        to = j;
        j++;
      } else if (along[j + 1]! - along[to + 1]! < REPEAT_BRIDGE_KM) {
        j++;
      } else break;
    }
    const km = along[to + 1]! - along[from]!;
    if (km >= MIN_REPEAT_KM) stretches.push({ from, to: to + 1, km });
    i = to + 1;
  }
  return stretches;
}

/**
 * Encode points using Google's polyline algorithm.
 *
 * Coordinates are delta-encoded and base64-ish packed, which turns a route's
 * waypoints into a short string — short enough to live in a shareable URL
 * without a server to store it against.
 *
 * `precision` 5 keeps roughly metre accuracy, which is far finer than anyone
 * places a waypoint by tapping a map.
 */
export function encodePolyline(points: LatLng[], precision = 5): string {
  const factor = 10 ** precision;
  let output = "";
  let lastLat = 0;
  let lastLng = 0;

  const encodeValue = (value: number): string => {
    let coordinate = value < 0 ? ~(value << 1) : value << 1;
    let chunk = "";
    while (coordinate >= 0x20) {
      chunk += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63);
      coordinate >>= 5;
    }
    return chunk + String.fromCharCode(coordinate + 63);
  };

  for (const point of points) {
    const lat = Math.round(point.lat * factor);
    const lng = Math.round(point.lng * factor);
    output += encodeValue(lat - lastLat) + encodeValue(lng - lastLng);
    lastLat = lat;
    lastLng = lng;
  }
  return output;
}

/**
 * Inverse of {@link encodePolyline}. Returns an empty list on malformed input.
 *
 * Junk is rejected up front rather than decoded: the algorithm reads arbitrary
 * bytes as varints quite happily, so an invalid string would otherwise yield
 * plausible-looking coordinates near null island instead of an obvious failure.
 */
export function decodePolyline(encoded: string, precision = 5): LatLng[] {
  // Encoded polylines only ever use printable ASCII from '?' (63) to '~' (126).
  for (let i = 0; i < encoded.length; i++) {
    const code = encoded.charCodeAt(i);
    if (code < 63 || code > 126) return [];
  }

  const factor = 10 ** precision;
  const points: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  try {
    while (index < encoded.length) {
      let result = 0;
      let shift = 0;
      let byte: number;

      // Latitude then longitude, each a varint of 5-bit groups.
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      lat += result & 1 ? ~(result >> 1) : result >> 1;

      result = 0;
      shift = 0;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      lng += result & 1 ? ~(result >> 1) : result >> 1;

      const point = { lat: lat / factor, lng: lng / factor };
      if (!Number.isFinite(point.lat) || Math.abs(point.lat) > 90) return [];
      if (!Number.isFinite(point.lng) || Math.abs(point.lng) > 180) return [];
      points.push(point);
    }
  } catch {
    return [];
  }

  return points;
}

export type GradeSample = { distanceKm: number; elevationM: number };

/**
 * Percent grade at each sample, measured across a centred distance window.
 *
 * Not from neighbouring points. A profile is typically sampled every 50–70 m
 * against a terrain model whose own resolution is around 90 m, so the rise
 * between two adjacent samples is dominated by grid noise rather than slope —
 * differencing them yields grades that swing by tens of percent over ground that
 * is genuinely uniform.
 *
 * A window wide enough to span several DEM cells measures the slope a walker
 * actually feels. It does flatten genuinely brief pitches, which is the right
 * trade: a short scramble is a trail feature, not a grade.
 */
export function gradeSeries(samples: GradeSample[], windowKm = 0.25): number[] {
  if (samples.length < 2) return samples.map(() => 0);

  const half = Math.max(windowKm, 1e-6) / 2;
  const grades: number[] = [];

  for (let i = 0; i < samples.length; i++) {
    const centre = samples[i]!.distanceKm;

    let low = i;
    while (low > 0 && centre - samples[low - 1]!.distanceKm <= half) low--;
    let high = i;
    while (high < samples.length - 1 && samples[high + 1]!.distanceKm - centre <= half) high++;

    // At the ends the window is one-sided; widen inward so it still spans real
    // ground rather than reporting the noise between two touching samples.
    if (low === high) {
      if (high < samples.length - 1) high++;
      else if (low > 0) low--;
    }

    const runKm = samples[high]!.distanceKm - samples[low]!.distanceKm;
    const riseM = samples[high]!.elevationM - samples[low]!.elevationM;
    grades.push(runKm > 0 ? riseM / (runKm * 10) : 0);
  }

  return grades;
}

/**
 * Distance-weighted mean grade over the ground that rises, as a percent.
 *
 * Not total gain over total distance. A hike returns to the car, so half an
 * out-and-back is descent, and dividing by the whole round trip halves the
 * figure into something nobody experiences. Halving the distance instead only
 * works because an out-and-back climbs on the way out; a loop can put its
 * climbing anywhere, and a rolling trail climbs repeatedly.
 *
 * Weighting by distance rather than by sample keeps a cluster of short steep
 * steps from outweighing a long steady pull, and using the same windowed grades
 * the chart is coloured from means the number and the picture agree.
 */
export function climbingGrade(samples: GradeSample[], windowKm = 0.25): number {
  if (samples.length < 2) return 0;

  const grades = gradeSeries(samples, windowKm);
  let weighted = 0;
  let distance = 0;

  for (let i = 1; i < samples.length; i++) {
    const km = Math.abs(samples[i]!.distanceKm - samples[i - 1]!.distanceKm);
    if (km <= 0) continue;

    // The grade of the ground just covered, from the window centred on it.
    const grade = ((grades[i] ?? 0) + (grades[i - 1] ?? 0)) / 2;
    if (grade <= 0) continue;

    weighted += grade * km;
    distance += km;
  }

  return distance > 0 ? weighted / distance : 0;
}
